import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCreateExamType, useExamTypes } from "@/hooks/useExamTypes";
import {
  useCreateExamPrescriptionTemplate,
  useExamPrescriptionTemplates,
  type ExamPrescriptionTemplateWithItems,
} from "@/hooks/useExamPrescriptionTemplates";
import {
  useCreateMedicalPrescription,
  useUpdateMedicalPrescription,
  type PrescriptionWithItems,
} from "@/hooks/useMedicalPrescriptions";
import { useProfileValidation } from "@/hooks/useProfileValidation";
import { validatePrescriptionProfile } from "@/lib/prescriptions/profile-validation";
import type { Profile } from "@/hooks/useProfile";
import { ProfileDataModal } from "./ProfileDataModal";
import { AddItemTrigger } from "./editor/AddItemTrigger";
import { ExamItemsEditor } from "./editor/ExamItemsEditor";
import { ItemCard } from "./editor/ItemCard";
import { ModelSearchField } from "./editor/ModelSearchField";
import { PrescriptionDetailsCollapsible } from "./editor/PrescriptionMetaFields";
import { SaveAsModelButton } from "./editor/SaveAsModelButton";
import { usePublishPrescriptionDraft } from "./editor/PrescriptionDraftContext";
import { useIsMobile } from "@/hooks/use-mobile";
import type { MedicalPrescriptionInput } from "@/lib/validations/medical-prescription";
import {
  examPrescriptionSchema,
  type ExamPrescriptionInput,
  type ExamPrescriptionItemInput,
} from "@/lib/validations/exam-prescription";
import {
  expandTemplateIntoPrescription,
  getTemplateDuplicateConflict,
  mapTemplateToPrescriptionItems,
  type TemplateDuplicateConflict,
} from "@/lib/prescriptions/exam-template-composition";

interface ExamPrescriptionComposerProps {
  petId: string;
  clinicId: string;
  appointmentId?: string;
  medicalRecordId?: string;
  onCreated?: (prescription?: PrescriptionWithItems | null) => void;
  onUpdated?: (prescriptionId?: string) => void;
  validityInDays?: number;
  onValidityInDaysChange?: (value: number | undefined) => void;
  showValidityField?: boolean;
  variant?: "desktop" | "mobile";
  initialPrescription?: PrescriptionWithItems | null;
}

interface InlineTemplateConflict {
  insertionIndex: number;
  conflict: TemplateDuplicateConflict;
}

const moveItem = <T,>(list: T[], from: number, to: number): T[] => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const isBlankExamPrescriptionItem = (item: ExamPrescriptionInput["items"][number]) =>
  !item.exam_type_id?.trim() &&
  !item.exam_name?.trim() &&
  !item.instructions?.trim();

export const ExamPrescriptionComposer = ({
  petId,
  clinicId,
  appointmentId,
  medicalRecordId,
  onCreated,
  onUpdated,
  validityInDays,
  onValidityInDaysChange,
  showValidityField = true,
  variant = "desktop",
  initialPrescription = null,
}: ExamPrescriptionComposerProps) => {
  const { t } = useTranslation("prescriptions");
  const [examSearch, setExamSearch] = useState("");
  const [showProfileDataModal, setShowProfileDataModal] = useState(false);
  const [pendingPrescriptionData, setPendingPrescriptionData] = useState<ExamPrescriptionInput | null>(null);
  const [templateConflict, setTemplateConflict] = useState<InlineTemplateConflict | null>(null);
  const [expanded, setExpanded] = useState<{ mode: "new" } | { mode: "edit"; index: number } | null>(null);
  const [draftRows, setDraftRows] = useState<ExamPrescriptionItemInput[]>([]);

  const form = useForm<ExamPrescriptionInput>({
    resolver: zodResolver(examPrescriptionSchema),
    defaultValues: {
      pet_id: petId,
      clinic_id: clinicId,
      appointment_id: appointmentId ?? "",
      medical_record_id: medicalRecordId ?? "",
      type: "exam",
      validity_in_days: validityInDays ?? 30,
      observations: "",
      items: [],
    },
  });

  const {
    fields: itemFields,
    append,
    remove,
    replace,
    update: updateItem,
  } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const currentValidity = form.watch("validity_in_days");

  useEffect(() => {
    if (validityInDays === currentValidity) return;
    form.setValue("validity_in_days", validityInDays, { shouldValidate: true });
  }, [validityInDays, currentValidity, form]);

  const { data: examTypes = [], isLoading: isLoadingExamTypes } = useExamTypes({
    searchTerm: examSearch,
    enabled: Boolean(clinicId),
  });
  const createExamType = useCreateExamType();
  const { data: examTemplates = [], isLoading: isLoadingTemplates } = useExamPrescriptionTemplates(clinicId);
  const createPrescription = useCreateMedicalPrescription();
  const updatePrescription = useUpdateMedicalPrescription();
  const validation = useProfileValidation("exam");
  const queryClient = useQueryClient();
  const isEditMode = Boolean(initialPrescription);

  const loadedRevision = useRef<string | null>(null);
  const resetExamForm = useCallback(() => {
    loadedRevision.current = initialPrescription?.updated_at ?? null;
    if (!initialPrescription) {
      form.reset({
        pet_id: petId,
        clinic_id: clinicId,
        appointment_id: appointmentId ?? "",
        medical_record_id: medicalRecordId ?? "",
        type: "exam",
        validity_in_days: validityInDays ?? 30,
        observations: "",
        items: [],
      });
      onValidityInDaysChange?.(validityInDays ?? 30);
    } else {
      const mappedItems = initialPrescription.exam_items.length > 0
        ? initialPrescription.exam_items.map((item) => ({
            exam_type_id: item.exam_type_id ?? "",
            exam_name: item.exam_name,
            instructions: item.instructions ?? "",
          }))
        : [];

      form.reset({
        pet_id: initialPrescription.pet_id,
        clinic_id: initialPrescription.clinic_id,
        appointment_id: initialPrescription.appointment_id ?? "",
        medical_record_id: initialPrescription.medical_record_id ?? "",
        type: "exam",
        validity_in_days: initialPrescription.validity_in_days ?? validityInDays ?? 30,
        observations: initialPrescription.observations ?? "",
        items: mappedItems,
      });
      onValidityInDaysChange?.(initialPrescription.validity_in_days ?? validityInDays ?? 30);
    }
    setExamSearch("");
    setTemplateConflict(null);
  }, [
    appointmentId,
    clinicId,
    form,
    initialPrescription,
    medicalRecordId,
    onValidityInDaysChange,
    petId,
    validityInDays,
  ]);

  useEffect(() => {
  }, [clinicId]);

  const initializedContext = useRef<string | null>(null);
  useEffect(() => {
    // Changing validity updates resetExamForm; it must not reset the item draft.
    const context = [petId, clinicId, appointmentId ?? "", medicalRecordId ?? "", initialPrescription?.id ?? "new"].join(":");
    if (initializedContext.current === context) return;
    initializedContext.current = context;
    resetExamForm();
  }, [resetExamForm, petId, clinicId, appointmentId, medicalRecordId, initialPrescription?.id]);

  const templatesById = useMemo(
    () => new Map(examTemplates.map((template) => [template.id, template])),
    [examTemplates],
  );

  const insertTemplateItemsAtIndex = (
    template: ExamPrescriptionTemplateWithItems,
    insertionIndex: number,
    duplicateResolution: "all" | "unique" = "all",
  ) => {
    const currentItems = form.getValues("items");
    const currentObservations = form.getValues("observations");

    const expanded = expandTemplateIntoPrescription({
      template,
      currentItems,
      currentObservations,
      duplicateResolution,
    });

    if (expanded.itemsToAdd.length === 0) {
      toast.info(t("examComposer.templateOnlyDuplicates"));
      return;
    }

    const nextItems = [
      ...currentItems.slice(0, insertionIndex),
      ...expanded.itemsToAdd,
      ...currentItems.slice(insertionIndex + 1),
    ];
    replace(nextItems);
    form.setValue("observations", expanded.nextObservations, { shouldDirty: true, shouldValidate: true });
    form.clearErrors("items");
    setExamSearch("");

    toast.success(
      t("examComposer.templateAdded", {
        template: template.title,
        count: expanded.itemsToAdd.length,
      }),
    );
  };

  const handleInlineTemplateSelection = (template: ExamPrescriptionTemplateWithItems, insertionIndex: number) => {
    const mappedItems = mapTemplateToPrescriptionItems(template);

    if (mappedItems.length === 0) {
      toast.info(t("examComposer.templateEmpty", { template: template.title }));
      return;
    }

    const conflict = getTemplateDuplicateConflict({
      template,
      currentItems: form.getValues("items"),
      currentObservations: form.getValues("observations"),
    });

    if (conflict) {
      setTemplateConflict({ insertionIndex, conflict });
      return;
    }

    insertTemplateItemsAtIndex(template, insertionIndex, "all");
  };

  const closeExpanded = () => {
    setExpanded(null);
    setDraftRows([]);
  };
  const commitRows = (rows: ExamPrescriptionItemInput[]) => {
    if (expanded?.mode === "edit") {
      updateItem(expanded.index, rows[0]);
    } else {
      append(rows);
    }
    form.clearErrors("items");
    closeExpanded();
  };

  const watchedExamItems = form.watch("items") ?? [];
  const publishedExamDraft = useMemo(
    () => ({
      values: { type: "exam", control_regime: "none", buyer_mode: "blank", items: [] } as unknown as MedicalPrescriptionInput,
      examItems: [...watchedExamItems, ...draftRows].map((row) => ({ exam_name: row.exam_name, instructions: row.instructions })),
      highlight: draftRows.length > 0 ? { kind: "exam" as const, index: watchedExamItems.length } : null,
    }),
    [watchedExamItems, draftRows],
  );
  const isMobileViewport = useIsMobile();
  usePublishPrescriptionDraft(publishedExamDraft, (variant === "mobile") === isMobileViewport);

  const handleSavePrescription = async (data: ExamPrescriptionInput, profileOverride?: Profile | null) => {
    const profileData = profileOverride ?? validation.profile;
    const crmvFull = profileData?.crmv_state && profileData?.crmv_number
      ? `${profileData.crmv_state}${profileData.crmv_number}`
      : null;

    const prescriptionPayload = {
      ...data,
      appointment_id: data.appointment_id || undefined,
      medical_record_id: data.medical_record_id && data.medical_record_id.trim() !== "" ? data.medical_record_id : undefined,
      crmv: crmvFull,
      mapa_number: profileData?.mapa_number || null,
    };

    if (initialPrescription) {
      const updatedPrescription = await updatePrescription.mutateAsync({
        ...prescriptionPayload,
        id: initialPrescription.id,
        expected_updated_at: loadedRevision.current,
        vet_user_id: initialPrescription.vet_user_id,
      });
      onUpdated?.(updatedPrescription?.id);
      return;
    }

    const newPrescription = await createPrescription.mutateAsync({
      ...prescriptionPayload,
      issuer_address: profileData?.address?.trim() || null,
    });

    form.reset({
      ...form.getValues(),
      observations: "",
      items: [],
    });
    setExpanded(null);
    setExamSearch("");
    setTemplateConflict(null);
    onCreated?.(newPrescription);
  };

  const onSubmit = async (data: ExamPrescriptionInput) => {
    if (expanded) {
      toast.info(t("editor.finishOpenItem"));
      return;
    }
    const validationResult = validation.validate();

    if (!validationResult.isValid) {
      setPendingPrescriptionData(data);
      setShowProfileDataModal(true);
      return;
    }

    await handleSavePrescription(data);
  };

  const handleExamFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    const currentItems = form.getValues("items");
    const filledItems = currentItems.filter((item) => !isBlankExamPrescriptionItem(item));

    if (filledItems.length > 0 && filledItems.length !== currentItems.length) {
      form.setValue("items", filledItems, { shouldDirty: true, shouldValidate: false });
      replace(filledItems);
    }

    void form.handleSubmit(onSubmit)(event);
  };

  const handleProfileDataComplete = async () => {
    if (!pendingPrescriptionData) return;

    setShowProfileDataModal(false);
    await new Promise((resolve) => setTimeout(resolve, 300));
    await queryClient.refetchQueries({ queryKey: ["profile"] });
    await new Promise((resolve) => setTimeout(resolve, 200));

    const profileFromCache = queryClient
      .getQueriesData<Profile | null>({ queryKey: ["profile"] })
      .map(([, profile]) => profile)
      .find((profile): profile is Profile => Boolean(profile));
    const refreshedValidation = validatePrescriptionProfile(profileFromCache, "exam");

    if (refreshedValidation.isValid) {
      await handleSavePrescription(pendingPrescriptionData, profileFromCache);
      setPendingPrescriptionData(null);
    } else {
      setShowProfileDataModal(true);
    }
  };

  const createExamTemplate = useCreateExamPrescriptionTemplate();
  /** Saves the whole exam list + guidance as one reusable model. */
  const saveWholeAsModel = async (title: string) => {
    const items = form.getValues("items").filter((item) => item.exam_name?.trim());
    if (items.length === 0) {
      toast.error(t("editor.saveModel.empty"));
      throw new Error("empty_model");
    }
    await createExamTemplate.mutateAsync({
      clinic_id: clinicId,
      title,
      observations: form.getValues("observations") ?? "",
      items,
    });
  };

  const examEditorProps = {
    mobile: variant === "mobile",
    examTypes,
    loadingExamTypes: isLoadingExamTypes,
    onSearchChange: setExamSearch,
    onCreateExamType: (name: string) => createExamType.mutateAsync({ name }),
    creatingExamType: createExamType.isPending,
    onCommit: commitRows,
    onCancel: closeExpanded,
    onDraftChange: setDraftRows,
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={handleExamFormSubmit} className="space-y-6">
          {examTemplates.length > 0 ? (
            <ModelSearchField
              label={t("editor.exam.model")}
              placeholder={t("editor.med.useModelPlaceholder")}
              items={examTemplates}
              getKey={(template) => template.id}
              getTitle={(template) => template.title}
              getDescription={(template) => (template.items ?? []).map((item) => item.exam_name).join(", ")}
              onPick={(template) => handleInlineTemplateSelection(template, form.getValues("items").length)}
              emptyText={t("examComposer.emptyTemplates")}
              loading={isLoadingTemplates}
            />
          ) : null}

          <div className="flex flex-col gap-2.5">
            {itemFields.map((item, index) =>
              expanded?.mode === "edit" && expanded.index === index ? (
                <ExamItemsEditor
                  key={`edit-${item.id}`}
                  initial={form.getValues(`items.${index}`)}
                  ordinal={index + 1}
                  {...examEditorProps}
                />
              ) : (
                <ItemCard
                  key={item.id}
                  index={index}
                  title={form.watch(`items.${index}.exam_name`) || t("examComposer.exam")}
                  summary={form.watch(`items.${index}.instructions`) || undefined}
                  mobile={variant === "mobile"}
                  onEdit={() => setExpanded({ mode: "edit", index })}
                  onRemove={() => remove(index)}
                  onMoveUp={index > 0 ? () => replace(moveItem(form.getValues("items"), index, index - 1)) : undefined}
                  onMoveDown={index < itemFields.length - 1 ? () => replace(moveItem(form.getValues("items"), index, index + 1)) : undefined}
                />
              ),
            )}
            {expanded?.mode === "new" ? (
              <ExamItemsEditor key="new-exams" initial={null} ordinal={itemFields.length + 1} {...examEditorProps} />
            ) : (
              <AddItemTrigger label={t("editor.exam.add")} mobile={variant === "mobile"} onClick={() => setExpanded({ mode: "new" })} />
            )}
            {form.formState.errors.items?.message || form.formState.errors.items?.root?.message ? (
              <p className="text-sm text-destructive" role="alert">
                {form.formState.errors.items?.message ?? form.formState.errors.items?.root?.message}
              </p>
            ) : null}
          </div>

          <FormField
            control={form.control}
            name="observations"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("examComposer.complementaryGuidance")}</FormLabel>
                <FormControl>
                  <Textarea rows={3} placeholder={t("examComposer.complementaryGuidancePlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showValidityField ? (
            <PrescriptionDetailsCollapsible
              mobile={variant === "mobile"}
              validity={form.watch("validity_in_days")}
              error={form.formState.errors.validity_in_days?.message}
              onValidityChange={(value) => {
                form.setValue("validity_in_days", value, { shouldValidate: true });
                onValidityInDaysChange?.(value);
              }}
            />
          ) : null}

          <SaveAsModelButton mobile={variant === "mobile"} onSave={saveWholeAsModel} />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={resetExamForm}
              disabled={createPrescription.isPending || updatePrescription.isPending}
            >
              {isEditMode ? t("composer.resetChanges") : t("clear", { ns: "common" })}
            </Button>
            <Button type="submit" disabled={createPrescription.isPending || updatePrescription.isPending}>
              {(createPrescription.isPending || updatePrescription.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEditMode ? t("composer.saveChanges") : t("composer.issuePrescription")}
            </Button>
          </div>
        </form>
      </Form>

      {showProfileDataModal && (
        <ProfileDataModal
          open={showProfileDataModal}
          onOpenChange={(open) => {
            if (!open) {
              setPendingPrescriptionData(null);
            }
            setShowProfileDataModal(open);
          }}
          needsCRMV={validation.validate().needsCRMV}
          needsMAPA={validation.validate().needsMAPA}
          needsAddress={validation.validate().needsAddress}
          onComplete={handleProfileDataComplete}
          contextLabel={t("examComposer.profileContextLabel")}
        />
      )}

      <AlertDialog
        open={Boolean(templateConflict)}
        onOpenChange={(open) => {
          if (!open) setTemplateConflict(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("examComposer.duplicateDialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {templateConflict
                ? t("examComposer.duplicateDialogDescription", { template: templateConflict.conflict.templateTitle })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {templateConflict ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("examComposer.duplicateListLabel")}</p>
              <div className="rounded-md border bg-muted/40 p-3">
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {templateConflict.conflict.duplicateExamNames.map((examName) => (
                    <li key={examName}>{examName}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}

          <AlertDialogFooter className="sm:justify-between">
            <AlertDialogCancel>{t("examComposer.cancelTemplateInsert")}</AlertDialogCancel>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <AlertDialogAction
                className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
                onClick={() => {
                  if (!templateConflict) return;
                  const template = templatesById.get(templateConflict.conflict.templateId);
                  if (!template) return;
                  insertTemplateItemsAtIndex(template, templateConflict.insertionIndex, "unique");
                  setTemplateConflict(null);
                }}
              >
                {t("examComposer.addOnlyUnique")}
              </AlertDialogAction>
              <AlertDialogAction
                onClick={() => {
                  if (!templateConflict) return;
                  const template = templatesById.get(templateConflict.conflict.templateId);
                  if (!template) return;
                  insertTemplateItemsAtIndex(template, templateConflict.insertionIndex, "all");
                  setTemplateConflict(null);
                }}
              >
                {t("examComposer.addAllDuplicates")}
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
